import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { gamesApi, usersApi, dealerSchedulerApi } from '../services/api';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEALER_COLORS = ['#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#c2410c', '#4f46e5'];

function DealerSchedule({ user, onLogout }) {
  // ====== URL state ======
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('view') || 'week'); // week | month | day | agenda
  const [weekOffset, setWeekOffset] = useState(parseInt(searchParams.get('w') || '0', 10));
  useEffect(() => {
    const p = new URLSearchParams(searchParams);
    p.set('view', view); p.set('w', String(weekOffset));
    setSearchParams(p, { replace: true });
  }, [view, weekOffset]);

  // ====== Data ======
  const [games, setGames] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [dealerLoad, setDealerLoad] = useState([]);
  const [shortages, setShortages] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // ====== UI state ======
  const [filterVenue, setFilterVenue] = useState('');
  const [filterDealer, setFilterDealer] = useState('');
  const [filterUnfilledOnly, setFilterUnfilledOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGames, setSelectedGames] = useState(new Set());
  const [draggingDealer, setDraggingDealer] = useState(null);
  const [compactMode, setCompactMode] = useState(localStorage.getItem('ds-compact') === 'true');
  const [hideWeekends, setHideWeekends] = useState(localStorage.getItem('ds-hideweekends') === 'true');
  const [contextMenu, setContextMenu] = useState(null); // { x, y, game, dealer }
  const [hoveredDealerId, setHoveredDealerId] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [lastAssignment, setLastAssignment] = useState(() => { try { return JSON.parse(localStorage.getItem('ds-last-assign')||'{}'); } catch { return {}; } });

  useEffect(() => { localStorage.setItem('ds-compact', String(compactMode)); }, [compactMode]);
  useEffect(() => { localStorage.setItem('ds-hideweekends', String(hideWeekends)); }, [hideWeekends]);

  // ====== Modals ======
  const [assignModal, setAssignModal] = useState(null); // { game }
  const [selectedDealer, setSelectedDealer] = useState('');
  const [selectedTable, setSelectedTable] = useState(1);
  const [recurring, setRecurring] = useState(true);
  const [recurringScope, setRecurringScope] = useState('future'); // future | this | 4weeks | all
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [assignNote, setAssignNote] = useState('');
  const [dealerSearch, setDealerSearch] = useState('');
  const [stayOpen, setStayOpen] = useState(false);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkDealer, setBulkDealer] = useState('');
  const [auditModal, setAuditModal] = useState(false);
  const [templateModal, setTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [broadcastModal, setBroadcastModal] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [commentsModal, setCommentsModal] = useState(null); // { game }
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // { msg, fn }
  const [busy, setBusy] = useState(false);

  // Toast / undo
  const [toast, setToast] = useState(null);
  const [undoStack, setUndoStack] = useState([]);

  const showToast = (msg, undoFn) => {
    setToast({ msg, undoFn });
    setTimeout(() => setToast(null), 6000);
  };

  // ====== Compute weekDates ======
  const weekDates = useMemo(() => {
    const now = new Date();
    const monday = new Date(now);
    const dayOfWeek = now.getDay();
    monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + (weekOffset * 7));
    monday.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
  }, [weekOffset]);
  const weekStartISO = weekDates[0]?.toISOString();

  // ====== Fetch ======
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [g, d, load, sh, al, tpl] = await Promise.all([
        gamesApi.getAll().catch(() => ({ data: { games: [] } })),
        usersApi.getDealers().catch(() => ({ data: { dealers: [] } })),
        weekStartISO ? dealerSchedulerApi.getDealerLoad(weekStartISO).catch(() => ({ data: { load: [] } })) : Promise.resolve({ data: { load: [] } }),
        weekStartISO ? dealerSchedulerApi.getWeekShortages(weekStartISO).catch(() => ({ data: { shortages: [] } })) : Promise.resolve({ data: { shortages: [] } }),
        dealerSchedulerApi.getAuditLog({ days: 7 }).catch(() => ({ data: { entries: [] } })),
        dealerSchedulerApi.listTemplates().catch(() => ({ data: { templates: [] } })),
      ]);
      setGames(g.data?.games || g.games || []);
      setDealers(d.data?.dealers || d.dealers || []);
      setDealerLoad(load.data?.load || load.load || []);
      setShortages(sh.data?.shortages || sh.shortages || []);
      setAuditLog(al.data?.entries || al.entries || []);
      setTemplates(tpl.data?.templates || tpl.templates || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [weekStartISO]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30s for live-ish updates
  useEffect(() => { const i = setInterval(fetchData, 30000); return () => clearInterval(i); }, [fetchData]);

  // ====== Keyboard shortcuts ======
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'j') setWeekOffset(w => w + 1);
      if (e.key === 'k') setWeekOffset(w => w - 1);
      if (e.key === 't') setWeekOffset(0);
      if (e.key === '/') { e.preventDefault(); document.getElementById('ds-search')?.focus(); }
      if (e.key === 'Escape') { setSelectedGames(new Set()); setAssignModal(null); setContextMenu(null); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && assignModal && selectedDealer) { e.preventDefault(); handleAssign(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ====== Helpers ======
  const formatTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Denver' });
  const formatDateShort = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const dealerById = useMemo(() => Object.fromEntries(dealers.map(d => [d.id, d])), [dealers]);
  const dealerColor = (id) => {
    const d = dealerById[id];
    if (d?.dealerColor) return d.dealerColor;
    const idx = dealers.findIndex(x => x.id === id);
    return DEALER_COLORS[idx % DEALER_COLORS.length] || '#6b7280';
  };
  const dealerName = (d) => {
    if (d.name) return d.name;
    const fn = d.firstName || d.first_name || '';
    const ln = d.lastName || d.last_name || '';
    return `${fn} ${ln[0] || ''}.`.trim().replace(/^\.$/, '');
  };

  // ====== Filtering ======
  const venues = useMemo(() => Array.from(new Set(games.map(g => g.venue?.name).filter(Boolean))).sort(), [games]);
  const filterGame = (g) => {
    if (filterVenue && g.venue?.name !== filterVenue) return false;
    if (filterDealer && !(g.dealers || []).some(d => d.id === filterDealer)) return false;
    if (filterUnfilledOnly && (g.dealers || []).length > 0) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const dealerNames = (g.dealers || []).map(d => `${d.firstName} ${d.lastName}`).join(' ').toLowerCase();
      if (!g.venue?.name?.toLowerCase().includes(q) && !dealerNames.includes(q)) return false;
    }
    return true;
  };

  // Group games by day-of-week index
  const gamesByDay = useMemo(() => {
    const acc = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    games.forEach(g => {
      const d = new Date(g.startTime || g.start_time);
      if (d >= weekDates[0] && d < new Date(weekDates[6].getTime() + 86400000)) {
        if (filterGame(g)) acc[d.getDay()].push(g);
      }
    });
    Object.values(acc).forEach(arr => arr.sort((a, b) => new Date(a.startTime || a.start_time) - new Date(b.startTime || b.start_time)));
    return acc;
  }, [games, weekDates, filterVenue, filterDealer, filterUnfilledOnly, searchQuery]);

  // Coverage stats
  const totalGames = games.filter(g => { const d = new Date(g.startTime || g.start_time); return d >= weekDates[0] && d < new Date(weekDates[6].getTime() + 86400000); }).length;
  const filledGames = totalGames - shortages.length;
  const coveragePct = totalGames ? Math.round((filledGames / totalGames) * 100) : 0;

  // ====== Actions ======
  const toggleSelect = (gameId) => {
    setSelectedGames(s => {
      const next = new Set(s);
      next.has(gameId) ? next.delete(gameId) : next.add(gameId);
      return next;
    });
  };

  // Live conflict check when dealer/game changes
  useEffect(() => {
    if (!assignModal || !selectedDealer) { setConflictWarning(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const c = await dealerSchedulerApi.checkConflicts(selectedDealer, assignModal.game.id);
        const conf = c.data || c;
        if (cancelled) return;
        if (conf.hasConflict) {
          setConflictWarning(`${conf.blocked ? '🚫 Dealer is marked OFF this date. ' : ''}${conf.doubleBooked?.length ? `⚠️ Double-booked with ${conf.doubleBooked.length} other game(s).` : ''}`.trim());
        } else setConflictWarning(null);
      } catch { setConflictWarning(null); }
    })();
    return () => { cancelled = true; };
  }, [selectedDealer, assignModal?.game?.id]);

  const handleAssign = async () => {
    if (!assignModal || !selectedDealer) return;
    setBusy(true);
    try {
      const includePast = recurringScope === 'all';
      const recur = recurringScope !== 'this';
      const r = await gamesApi.assignDealerToTable(assignModal.game.id, selectedDealer, selectedTable, { recurring: recur, includePast, replaceTable: replaceExisting });
      const propagated = r.data?.propagatedToFutureGames || 0;
      // Save last assignment as default for next modal open
      const last = { dealer: selectedDealer, table: selectedTable, scope: recurringScope };
      setLastAssignment(last); localStorage.setItem('ds-last-assign', JSON.stringify(last));
      // Toast with undo
      showToast(`Assigned${propagated > 0 ? ` (+ ${propagated} games)` : ''}${assignNote ? ' · note saved' : ''}`, async () => {
        await gamesApi.removeDealerFromGame(assignModal.game.id, selectedDealer);
        fetchData();
      });
      // Save note as comment
      if (assignNote.trim()) {
        try { await dealerSchedulerApi.addComment(assignModal.game.id, user?.id, `[Assignment] ${assignNote}`); } catch {}
      }
      // Stay open → jump to next unfilled game; otherwise close
      if (stayOpen) {
        const nextGame = nextUnfilledGame(assignModal.game.id);
        if (nextGame) {
          setAssignModal({ game: nextGame });
          setSelectedDealer(''); setAssignNote(''); setConflictWarning(null);
        } else {
          showToast('All games filled ✓');
          setAssignModal(null);
        }
      } else {
        setAssignModal(null);
        setSelectedDealer(''); setSelectedTable(1); setAssignNote(''); setConflictWarning(null); setReplaceExisting(false);
      }
      fetchData();
    } catch (e) { alert('Failed: ' + (e.message || 'unknown')); }
    finally { setBusy(false); }
  };

  const nextUnfilledGame = (afterId) => {
    const all = [];
    Object.values(gamesByDay).forEach(arr => arr.forEach(g => all.push(g)));
    const idx = all.findIndex(g => g.id === afterId);
    for (let i = idx + 1; i < all.length; i++) {
      if ((all[i].dealers || []).length === 0) return all[i];
    }
    return null;
  };

  const handleRemoveDealer = async (gameId, dealerId, name) => {
    setConfirmAction({
      msg: `Remove ${name} from this game?`,
      fn: async () => {
        try {
          await gamesApi.removeDealerFromGame(gameId, dealerId);
          showToast(`Removed ${name}`);
          fetchData();
        } catch (e) { alert('Failed: ' + e.message); }
      },
    });
  };

  const handleBulkAssign = async () => {
    if (selectedGames.size === 0 || !bulkDealer) return;
    setBusy(true);
    try {
      const r = await dealerSchedulerApi.bulkAssign([...selectedGames], bulkDealer, null, user?.id);
      setBulkModal(false); setBulkDealer(''); setSelectedGames(new Set());
      showToast(`Assigned to ${r.data?.assigned || 0} games`);
      fetchData();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const handleBulkClear = () => {
    setConfirmAction({
      msg: `Clear all dealers from ${selectedGames.size} selected games?`,
      fn: async () => {
        try {
          const r = await dealerSchedulerApi.bulkClear([...selectedGames], user?.id);
          setSelectedGames(new Set());
          showToast(`Cleared ${r.data?.cleared || 0} assignments`);
          fetchData();
        } catch (e) { alert('Failed: ' + e.message); }
      },
    });
  };

  const handleAutoFill = () => {
    setConfirmAction({
      msg: `Auto-fill all empty slots this week using rotation?`,
      fn: async () => {
        setBusy(true);
        try {
          const r = await dealerSchedulerApi.autoFillWeek(weekStartISO, user?.id);
          showToast(`Filled ${r.data?.filled || 0} of ${r.data?.totalUnfilled || 0} open slots`);
          fetchData();
        } catch (e) { alert('Failed: ' + e.message); }
        finally { setBusy(false); }
      },
    });
  };

  const handleCopyWeek = () => {
    setConfirmAction({
      msg: `Copy assignments from previous week to this week?`,
      fn: async () => {
        setBusy(true);
        try {
          const prev = new Date(weekDates[0]); prev.setDate(prev.getDate() - 7);
          const r = await dealerSchedulerApi.copyWeek(prev.toISOString(), weekStartISO, user?.id);
          showToast(`Copied ${r.data?.copied || 0} assignments`);
          fetchData();
        } catch (e) { alert('Failed: ' + e.message); }
        finally { setBusy(false); }
      },
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    const payload = [];
    for (const g of games) {
      const d = new Date(g.startTime || g.start_time);
      if (d < weekDates[0] || d >= new Date(weekDates[6].getTime() + 86400000)) continue;
      for (const dr of (g.dealers || [])) {
        payload.push({
          dayOfWeek: (d.getDay() === 0 ? 6 : d.getDay() - 1),
          time: d.toTimeString().slice(0, 5),
          venue_id: g.venue?.id,
          dealer_id: dr.id,
          table_number: dr.tableNumber || null,
        });
      }
    }
    try {
      await dealerSchedulerApi.saveTemplate(templateName, '', payload, user?.id);
      setTemplateModal(false); setTemplateName('');
      showToast('Template saved');
      fetchData();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const handleApplyTemplate = async (templateId) => {
    setBusy(true);
    try {
      const r = await dealerSchedulerApi.applyTemplate(templateId, weekStartISO, user?.id);
      showToast(`Applied: ${r.data?.applied || 0} assignments`);
      fetchData();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const handleBroadcast = async () => {
    if (!broadcastTitle.trim()) return;
    try {
      await dealerSchedulerApi.broadcast(dealers.map(d => d.id), broadcastTitle, broadcastBody);
      setBroadcastModal(false); setBroadcastTitle(''); setBroadcastBody('');
      showToast(`Broadcast sent to ${dealers.length} dealers`);
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const openComments = async (game) => {
    setCommentsModal({ game });
    try {
      const r = await dealerSchedulerApi.getComments(game.id);
      setComments(r.data?.comments || r.comments || []);
    } catch {}
  };

  const addComment = async () => {
    if (!commentsModal || !newComment.trim()) return;
    try {
      await dealerSchedulerApi.addComment(commentsModal.game.id, user?.id, newComment);
      setNewComment('');
      const r = await dealerSchedulerApi.getComments(commentsModal.game.id);
      setComments(r.data?.comments || r.comments || []);
    } catch (e) { alert('Failed: ' + e.message); }
  };

  // Drag-drop
  const onDragStartDealer = (d) => setDraggingDealer(d);
  const onDropOnGame = async (game) => {
    if (!draggingDealer) return;
    // Open assign modal pre-filled with the dragged dealer
    setAssignModal({ game });
    setSelectedDealer(draggingDealer.id);
    setSelectedTable(1);
    setRecurringScope('this');
    setDealerSearch('');
    setDraggingDealer(null);
  };

  // Print
  const printSchedule = () => window.print();

  // CSV export
  const exportCSV = () => {
    const rows = [['Date', 'Time', 'Venue', 'Dealers']];
    games.forEach(g => {
      const d = new Date(g.startTime || g.start_time);
      if (d < weekDates[0] || d >= new Date(weekDates[6].getTime() + 86400000)) return;
      rows.push([d.toLocaleDateString(), formatTime(d), g.venue?.name || '', (g.dealers || []).map(dr => `${dr.firstName} ${dr.lastName}`).join('; ')]);
    });
    const csv = rows.map(r => r.map(c => `"${(c||'').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dealer-schedule-${weekDates[0].toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // ===== Game card component =====
  const GameCard = ({ game }) => {
    const dealers = game.dealers || [];
    const byTable = {};
    dealers.forEach(d => { const k = d.tableNumber || 0; if (!byTable[k]) byTable[k] = []; byTable[k].push(d); });
    const hasDealer = dealers.length > 0;
    const isShortage = shortages.some(s => s.id === game.id);
    const isSelected = selectedGames.has(game.id);
    return (
      <div
        draggable={false}
        onDragOver={e => e.preventDefault()}
        onDrop={() => onDropOnGame(game)}
        style={{
          background: isSelected ? '#dbeafe' : (hasDealer ? '#f0fdf4' : '#fef3c7'),
          border: `${isSelected ? 2 : 1}px solid ${isSelected ? '#2563eb' : (hasDealer ? '#86efac' : '#fcd34d')}`,
          borderRadius: 8, padding: 8, marginBottom: 6, fontSize: 12, cursor: 'pointer', position: 'relative',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <input type="checkbox" checked={isSelected} onChange={e => { e.stopPropagation(); toggleSelect(game.id); }} onClick={e => e.stopPropagation()} />
          {isShortage && <span style={{ color: '#dc2626', fontSize: 10 }}>⚠️ short</span>}
        </div>
        <div onClick={() => { setAssignModal({ game }); setSelectedDealer(''); }}>
          <div style={{ fontWeight: 600 }}>{game.venue?.name || '-'}</div>
          <div style={{ color: '#666' }}>{formatTime(game.startTime || game.start_time)}</div>
          {hasDealer ? (
            <div style={{ marginTop: 4 }}>
              {Object.keys(byTable).sort((a,b)=>a-b).map(tn => (
                <div key={tn} style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 2 }}>
                  {tn !== '0' && <span style={{ background: '#16a34a', color: '#fff', padding: '1px 5px', borderRadius: 4, fontSize: 10 }}>T{tn}</span>}
                  {byTable[tn].map(d => (
                    <span key={d.id}
                      onClick={(e) => { e.stopPropagation(); handleRemoveDealer(game.id, d.id, dealerName(d)); }}
                      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, game, dealer: d }); }}
                      onMouseEnter={() => setHoveredDealerId(d.id)}
                      onMouseLeave={() => setHoveredDealerId(null)}
                      title="Click=remove · Right-click=menu"
                      style={{ background: dealerColor(d.id), color: '#fff', padding: '3px 8px', borderRadius: 10, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}>
                      {dealerName(d) || '?'} ✕
                    </span>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ marginTop: 4, color: '#d97706', fontWeight: 600, fontSize: 11 }}>No dealer</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <button onClick={(e) => { e.stopPropagation(); openComments(game); }} title="Comments"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11 }}>💬</button>
        </div>
      </div>
    );
  };

  // Filter weekDates to hide weekends
  const visibleWeekDates = hideWeekends ? weekDates.filter(d => d.getDay() !== 0 && d.getDay() !== 6) : weekDates;
  // Workload warning threshold
  const overworked = dealerLoad.filter(l => parseInt(l.game_count) > 7);

  // Render
  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h2>Dealer Schedule</h2>
            <p>Drag dealers from sidebar · J/K to navigate · T for today · / to search</p>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <input id="ds-search" placeholder="🔎 Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc' }} />
            <button className="btn btn-secondary" onClick={() => setWeekOffset(w => w - 1)} style={{ position: 'relative' }}>
              ← {shortages.length > 0 && weekOffset >= 0 && <span style={{ position: 'absolute', top: -4, right: -4, background: '#dc2626', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>!</span>}
            </button>
            <button className="btn btn-secondary" onClick={() => setWeekOffset(0)}>Today</button>
            <button className="btn btn-secondary" onClick={() => setWeekOffset(w => w + 1)} style={{ position: 'relative' }}>
              → {shortages.length > 0 && weekOffset <= 0 && <span style={{ position: 'absolute', top: -4, right: -4, background: '#dc2626', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>!</span>}
            </button>
            <button className="btn btn-secondary" onClick={() => setCompactMode(!compactMode)} title="Compact mode">{compactMode ? '🔍' : '⊟'}</button>
            <button className="btn btn-secondary" onClick={() => setHideWeekends(!hideWeekends)} title="Hide weekends">{hideWeekends ? '7' : '5'}d</button>
            <button className="btn btn-secondary" onClick={() => setLegendOpen(!legendOpen)} title="Legend">?</button>
            <select value={view} onChange={e => setView(e.target.value)} style={{ padding: 6, borderRadius: 6 }}>
              <option value="week">Week</option>
              <option value="agenda">Agenda</option>
              <option value="month">Month</option>
            </select>
            <button className="btn btn-primary" onClick={handleAutoFill} disabled={busy}>✨ Auto-fill</button>
            <button className="btn btn-secondary" onClick={handleCopyWeek} disabled={busy}>📋 Copy from prev</button>
            <button className="btn btn-secondary" onClick={() => setTemplateModal(true)}>📁 Templates</button>
            <button className="btn btn-secondary" onClick={() => setAuditModal(true)}>📜 Audit</button>
            <button className="btn btn-secondary" onClick={() => setBroadcastModal(true)}>📣 Broadcast</button>
            <button className="btn btn-secondary" onClick={exportCSV}>⬇ CSV</button>
            <button className="btn btn-secondary" onClick={printSchedule}>🖨</button>
          </div>
        </div>

        {/* Coverage stats bar with circular gauge */}
        <div style={{ display: 'flex', gap: 16, padding: 12, background: '#f9fafb', borderRadius: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Circular gauge */}
          <div style={{ position: 'relative', width: 60, height: 60 }}>
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r="26" fill="none" stroke="#e5e7eb" strokeWidth="6" />
              <circle cx="30" cy="30" r="26" fill="none"
                stroke={coveragePct > 80 ? '#16a34a' : coveragePct > 50 ? '#d97706' : '#dc2626'}
                strokeWidth="6" strokeLinecap="round"
                strokeDasharray={`${(coveragePct/100) * 163.4} 163.4`}
                transform="rotate(-90 30 30)" />
            </svg>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>{coveragePct}%</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Coverage</div>
            <div><strong>{filledGames}</strong> / {totalGames} filled</div>
          </div>
          {shortages.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Shortages</div>
              <div style={{ color: '#dc2626', fontWeight: 700 }}>⚠️ {shortages.length}</div>
            </div>
          )}
          {overworked.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: '#666' }}>Overworked</div>
              <div style={{ color: '#d97706', fontWeight: 700 }}>{overworked.length} dealer{overworked.length !== 1 ? 's' : ''} &gt;7 games</div>
            </div>
          )}
          <span style={{ color: '#666', marginLeft: 'auto' }}>{formatDateShort(weekDates[0])} – {formatDateShort(weekDates[6])}</span>
        </div>

        {/* Legend popover */}
        {legendOpen && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
            <strong>Legend:</strong>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 }}>
              <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#f0fdf4', border: '1px solid #86efac' }}></span> Filled</span>
              <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#fef3c7', border: '1px solid #fcd34d' }}></span> No dealer</span>
              <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#dbeafe', border: '1px solid #2563eb' }}></span> Selected</span>
              <span>⚠️ Shortage</span>
              <span>🔴 Overworked</span>
              <span>Drag dealer chip → game card</span>
              <span>Right-click chip for context menu</span>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <select value={filterVenue} onChange={e => setFilterVenue(e.target.value)} style={{ padding: 6 }}>
            <option value="">All venues</option>
            {venues.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={filterDealer} onChange={e => setFilterDealer(e.target.value)} style={{ padding: 6 }}>
            <option value="">All dealers</option>
            {dealers.map(d => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={filterUnfilledOnly} onChange={e => setFilterUnfilledOnly(e.target.checked)} />
            Unfilled only
          </label>
          {selectedGames.size > 0 && (
            <>
              <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{selectedGames.size} selected</span>
              <button className="btn btn-primary btn-sm" onClick={() => setBulkModal(true)}>Bulk assign</button>
              <button className="btn btn-danger btn-sm" onClick={handleBulkClear}>Bulk clear</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedGames(new Set())}>Clear sel</button>
            </>
          )}
        </div>

        {loading ? (
          <div className="loading"><div className="loading-spinner"></div></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12 }}>
            {/* Sidebar: dealer chips + load chart */}
            <div>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Dealers</div>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>Drag onto a game card</div>
                {dealers.map(d => {
                  const load = dealerLoad.find(l => l.id === d.id);
                  const count = parseInt(load?.game_count || 0);
                  return (
                    <div key={d.id} draggable onDragStart={() => onDragStartDealer(d)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 6, marginBottom: 4, background: '#f9fafb', borderRadius: 6, cursor: 'grab' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: dealerColor(d.id) }}></span>
                        {dealerName(d)}
                      </span>
                      <span style={{ fontSize: 11, color: '#666' }}>{count}</span>
                    </div>
                  );
                })}
              </div>
              {/* Pending check-ins */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>⚠️ Shortages</div>
                {shortages.length === 0 ? <div style={{ fontSize: 11, color: '#16a34a' }}>None this week ✓</div> :
                  shortages.slice(0, 10).map(s => (
                    <div key={s.id} style={{ fontSize: 11, color: '#dc2626', marginBottom: 4 }}>
                      {new Date(s.start_time).toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric' })} {s.venue_name}
                    </div>
                  ))}
              </div>
            </div>

            {/* Main view */}
            <div>
              {view === 'week' && (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${visibleWeekDates.length}, 1fr)`, gap: compactMode ? 4 : 8 }}>
                  {visibleWeekDates.map((date, dayIdx) => {
                    const isToday = new Date().toDateString() === date.toDateString();
                    return (
                      <div key={dayIdx} style={{ background: isToday ? '#f0f7ff' : '#fff', border: isToday ? '2px solid #2563eb' : '1px solid #e5e7eb', borderRadius: 12, padding: 12, minHeight: 200 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: isToday ? '#2563eb' : '#333' }}>{SHORT_DAYS[date.getDay()]}</div>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{formatDateShort(date)}</div>
                        {gamesByDay[date.getDay()]?.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#ccc', textAlign: 'center', marginTop: 20 }}>No games</div>
                        ) : gamesByDay[date.getDay()].map(g => <GameCard key={g.id} game={g} />)}
                      </div>
                    );
                  })}
                </div>
              )}

              {view === 'agenda' && (
                <div>
                  {weekDates.map((date, dayIdx) => {
                    const dayGames = gamesByDay[date.getDay()] || [];
                    if (dayGames.length === 0) return null;
                    return (
                      <div key={dayIdx} style={{ marginBottom: 16 }}>
                        <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>{date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
                        {dayGames.map(g => <div key={g.id} style={{ padding: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{formatTime(g.startTime || g.start_time)} · {g.venue?.name}</strong>
                            <div>
                              {(g.dealers || []).map(d => (
                                <span key={d.id} style={{ background: dealerColor(d.id), color: '#fff', padding: '2px 8px', borderRadius: 12, marginLeft: 4, fontSize: 12 }}>
                                  {dealerName(d)}{d.tableNumber ? ` T${d.tableNumber}` : ''}
                                </span>
                              ))}
                              {(g.dealers || []).length === 0 && <span style={{ color: '#dc2626' }}>No dealer</span>}
                            </div>
                          </div>
                        </div>)}
                      </div>
                    );
                  })}
                </div>
              )}

              {view === 'month' && (
                <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                  Month view — under development. Use Week or Agenda for now.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============ Modals ============ */}

        {/* Assign modal — upgraded */}
        {assignModal && (
          <Modal onClose={() => setAssignModal(null)} title={`Assign Dealer — ${assignModal.game.venue?.name}`}>
            <p style={{ color: '#666' }}>{new Date(assignModal.game.startTime || assignModal.game.start_time).toLocaleString('en-US', { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>

            {(assignModal.game.dealers || []).length > 0 && (
              <div style={{ background: '#f0fdf4', padding: 12, borderRadius: 6, marginBottom: 12, border: '1px solid #86efac' }}>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>✓ Currently assigned:</div>
                {assignModal.game.dealers.map(d => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 4 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: dealerColor(d.id) }}></span>
                      {dealerName(d)}{d.tableNumber ? ` · Table ${d.tableNumber}` : ''}
                    </span>
                    <button className="btn btn-danger btn-sm" onClick={async () => {
                      try { await gamesApi.removeDealerFromGame(assignModal.game.id, d.id); showToast(`Removed ${dealerName(d)}`); fetchData();
                        // Update modal in-place
                        setAssignModal({ game: { ...assignModal.game, dealers: assignModal.game.dealers.filter(x => x.id !== d.id) } });
                      } catch (e) { alert(e.message); }
                    }}>✕ Remove</button>
                  </div>
                ))}
              </div>
            )}

            <label style={{ fontSize: 13, fontWeight: 600 }}>Search dealer:</label>
            <input value={dealerSearch} onChange={e => setDealerSearch(e.target.value)} placeholder="Type to filter..."
              style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginBottom: 8 }} autoFocus />

            <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, marginBottom: 10 }}>
              {dealers
                .filter(d => !dealerSearch || `${d.firstName} ${d.lastName}`.toLowerCase().includes(dealerSearch.toLowerCase()))
                .map(d => {
                  const isBlocked = false; // could call /availability per dealer; skip for perf
                  return (
                    <div key={d.id} onClick={() => setSelectedDealer(d.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: 8, cursor: 'pointer',
                        background: selectedDealer === d.id ? '#dbeafe' : 'transparent',
                        opacity: isBlocked ? 0.4 : 1,
                      }}>
                      <span style={{ width: 14, height: 14, borderRadius: '50%', background: dealerColor(d.id) }}></span>
                      <span>{d.firstName} {d.lastName}</span>
                    </div>
                  );
                })}
            </div>

            {conflictWarning && (
              <div style={{ background: '#fef2f2', border: '1px solid #dc2626', color: '#dc2626', padding: 10, borderRadius: 6, marginBottom: 10, fontSize: 13 }}>
                {conflictWarning}
              </div>
            )}

            <label style={{ fontSize: 13, fontWeight: 600 }}>Table:</label>
            <select className="form-select" value={selectedTable} onChange={e => setSelectedTable(parseInt(e.target.value))} style={{ marginBottom: 8 }}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>Table {n}</option>)}
            </select>

            <label style={{ fontSize: 13, fontWeight: 600 }}>Recurring scope:</label>
            <select className="form-select" value={recurringScope} onChange={e => setRecurringScope(e.target.value)} style={{ marginBottom: 8 }}>
              <option value="this">📍 This game only</option>
              <option value="future">🔁 All future matching games</option>
              <option value="all">⏪ All past + future games</option>
            </select>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 6 }}>
              <input type="checkbox" checked={replaceExisting} onChange={e => setReplaceExisting(e.target.checked)} />
              ♻️ Replace existing dealer on this table
            </label>

            <label style={{ fontSize: 13, fontWeight: 600 }}>Note (optional):</label>
            <textarea value={assignNote} onChange={e => setAssignNote(e.target.value)} rows={2} placeholder="e.g. needs deck refresh"
              style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid #ccc', marginBottom: 8 }} />

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={stayOpen} onChange={e => setStayOpen(e.target.checked)} />
              ⏭ Save & jump to next unfilled
            </label>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setAssignModal(null)} style={{ flex: 1 }}>Cancel (Esc)</button>
              <button className="btn btn-primary" onClick={handleAssign} disabled={!selectedDealer || busy} style={{ flex: 1 }}>
                {busy ? 'Saving...' : 'Assign (⌘↵)'}
              </button>
            </div>
          </Modal>
        )}

        {/* Bulk assign modal */}
        {bulkModal && (
          <Modal onClose={() => setBulkModal(false)} title={`Bulk Assign · ${selectedGames.size} games`}>
            <label>Dealer:</label>
            <select className="form-select" value={bulkDealer} onChange={e => setBulkDealer(e.target.value)}>
              <option value="">Choose...</option>
              {dealers.map(d => <option key={d.id} value={d.id}>{d.firstName} {d.lastName}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setBulkModal(false)} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkAssign} disabled={!bulkDealer || busy} style={{ flex: 1 }}>Assign</button>
            </div>
          </Modal>
        )}

        {/* Audit modal */}
        {auditModal && (
          <Modal onClose={() => setAuditModal(false)} title="Audit Log (last 7 days)">
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              {auditLog.length === 0 ? <p>No entries</p> : auditLog.map(e => (
                <div key={e.id} style={{ padding: 8, borderBottom: '1px solid #eee', fontSize: 12 }}>
                  <div><strong>{e.action}</strong> by {e.actor_fn || 'system'} {e.actor_ln || ''}</div>
                  <div style={{ color: '#666' }}>
                    {e.dealer_fn ? `Dealer: ${e.dealer_fn} ${e.dealer_ln} · ` : ''}{new Date(e.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </Modal>
        )}

        {/* Templates modal */}
        {templateModal && (
          <Modal onClose={() => setTemplateModal(false)} title="Schedule Templates">
            <div style={{ marginBottom: 16 }}>
              <h4>Save current week as template:</h4>
              <input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name..." style={{ width: '100%', padding: 8, marginBottom: 8 }} />
              <button className="btn btn-primary" onClick={handleSaveTemplate} disabled={!templateName.trim()}>💾 Save</button>
            </div>
            <h4>Apply existing:</h4>
            {templates.length === 0 ? <p>No templates yet</p> : templates.map(t => (
              <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: 8, border: '1px solid #eee', borderRadius: 6, marginBottom: 6 }}>
                <span>{t.name}</span>
                <button className="btn btn-primary btn-sm" onClick={() => handleApplyTemplate(t.id)} disabled={busy}>Apply to this week</button>
              </div>
            ))}
          </Modal>
        )}

        {/* Broadcast modal */}
        {broadcastModal && (
          <Modal onClose={() => setBroadcastModal(false)} title={`📣 Broadcast to ${dealers.length} dealers`}>
            <label>Title:</label>
            <input value={broadcastTitle} onChange={e => setBroadcastTitle(e.target.value)} style={{ width: '100%', padding: 8, marginBottom: 8 }} />
            <label>Message:</label>
            <textarea value={broadcastBody} onChange={e => setBroadcastBody(e.target.value)} rows={5} style={{ width: '100%', padding: 8 }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setBroadcastModal(false)} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBroadcast} disabled={!broadcastTitle.trim()} style={{ flex: 1 }}>Send</button>
            </div>
          </Modal>
        )}

        {/* Comments modal */}
        {commentsModal && (
          <Modal onClose={() => setCommentsModal(null)} title={`💬 Comments — ${commentsModal.game.venue?.name}`}>
            <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
              {comments.length === 0 ? <p>No comments yet</p> : comments.map(c => (
                <div key={c.id} style={{ padding: 8, background: '#f9fafb', borderRadius: 6, marginBottom: 6 }}>
                  <div>{c.body}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>{c.first_name} {c.last_name} · {new Date(c.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment..." rows={3} style={{ width: '100%', padding: 8 }} />
            <button className="btn btn-primary" onClick={addComment} disabled={!newComment.trim()} style={{ marginTop: 8 }}>Post</button>
          </Modal>
        )}

        {/* Confirm action */}
        {confirmAction && (
          <Modal onClose={() => setConfirmAction(null)} title="Confirm">
            <p>{confirmAction.msg}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-danger" onClick={() => { confirmAction.fn(); setConfirmAction(null); }} style={{ flex: 1 }}>Confirm</button>
            </div>
          </Modal>
        )}

        {/* Right-click context menu */}
        {contextMenu && (
          <>
            <div onClick={() => setContextMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
            <div style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, minWidth: 160 }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontWeight: 600 }}>{dealerName(contextMenu.dealer)}</div>
              <button style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                onClick={() => { handleRemoveDealer(contextMenu.game.id, contextMenu.dealer.id, dealerName(contextMenu.dealer)); setContextMenu(null); }}>
                ✕ Remove from this game
              </button>
              <button style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                onClick={() => { setAssignModal({ game: contextMenu.game }); setSelectedDealer(''); setContextMenu(null); }}>
                ↻ Replace with another dealer
              </button>
              <button style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                onClick={() => { setFilterDealer(contextMenu.dealer.id); setContextMenu(null); }}>
                👁 Show only this dealer's games
              </button>
              <button style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer' }}
                onClick={async () => { await dealerSchedulerApi.notifyDealer(contextMenu.dealer.id, 'Schedule update', `You're assigned to ${contextMenu.game.venue?.name} on ${new Date(contextMenu.game.startTime).toLocaleString()}`); showToast('Notified'); setContextMenu(null); }}>
                🔔 Notify this dealer
              </button>
            </div>
          </>
        )}

        {/* Hover tooltip for dealer */}
        {hoveredDealerId && (
          <div style={{ position: 'fixed', bottom: 80, right: 20, background: '#1a2a3e', color: '#fff', padding: 12, borderRadius: 8, fontSize: 12, maxWidth: 250, zIndex: 999 }}>
            <strong>{dealerName(dealers.find(d => d.id === hoveredDealerId) || {})}</strong>
            <div style={{ marginTop: 4, color: '#aaa' }}>This week: {dealerLoad.find(l => l.id === hoveredDealerId)?.game_count || 0} games</div>
          </div>
        )}

        {/* Toast with undo */}
        {toast && (
          <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1a2a3e', color: '#fff', padding: '12px 20px', borderRadius: 8, display: 'flex', gap: 12, alignItems: 'center', zIndex: 1000 }}>
            <span>{toast.msg}</span>
            {toast.undoFn && <button className="btn btn-secondary btn-sm" onClick={() => { toast.undoFn(); setToast(null); }}>Undo</button>}
          </div>
        )}
      </main>
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header"><h3>{title}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export default DealerSchedule;
